import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Apple, Power, Replace, ShieldCheck, Smartphone, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { AndroidForm, emptyAndroid, emptyIos, IosForm } from '@/components/credentials/credential-forms';
import { EmptyState } from '@/components/shared/empty-state';
import { LoadingButton } from '@/components/shared/loading-button';
import { SectionCard } from '@/components/shared/section-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useToast } from '@/lib/toast';
import type { AppEnvironment } from '@/lib/types';
import { useAppCtx } from './app-layout';

type Platform = 'android' | 'ios';

export function Credentials() {
  const { app, env } = useAppCtx();
  const qc = useQueryClient();
  const toast = useToast();
  const environment = app.environments.find((e) => e.environment === env);
  const refresh = () => qc.invalidateQueries({ queryKey: ['app', app.id] });

  const createEnv = useMutation({
    mutationFn: () => api(`/admin/v1/apps/${app.id}/environments`, { method: 'POST', body: { environment: env } }),
    onSuccess: refresh,
  });

  if (!environment) {
    return (
      <Card>
        <EmptyState
          title={`No ${env} environment yet`}
          hint="Create it to upload push credentials for this environment."
          action={<LoadingButton loading={createEnv.isPending} onClick={() => createEnv.mutate()}>Create {env} environment</LoadingButton>}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {env === 'production' && (
        <Alert className="border-warning/40 bg-warning/10">
          <TriangleAlert className="text-amber-600 dark:text-warning" />
          <AlertDescription className="text-foreground">Production credentials push to live users. Changes take effect for new sends within 15 minutes.</AlertDescription>
        </Alert>
      )}
      <PlatformCard platform="android" environment={environment} appId={app.id} onChanged={refresh} toast={toast} />
      <PlatformCard platform="ios" environment={environment} appId={app.id} onChanged={refresh} toast={toast} />
    </div>
  );
}

function PlatformCard({ platform, environment, appId, onChanged, toast }: { platform: Platform; environment: AppEnvironment; appId: string; onChanged(): void; toast: ReturnType<typeof useToast> }) {
  const current = environment[platform];
  const [editing, setEditing] = useState(!current);
  const [android, setAndroid] = useState(emptyAndroid());
  const [ios, setIos] = useState(emptyIos());
  const base = `/admin/v1/apps/${appId}/environments/${environment.environment}`;

  const save = useMutation({
    mutationFn: () => api(`${base}/${platform}`, { method: 'PUT', body: platform === 'android' ? android : ios }),
    onSuccess: () => { setEditing(false); onChanged(); toast('Credentials saved — run Verify to check them'); },
  });
  const verify = useMutation({
    mutationFn: () => api<{ valid: boolean; message: string }>(`${base}/${platform}/verify`, { method: 'POST' }),
    onSuccess: (r) => { onChanged(); toast(r.message, r.valid ? 'ok' : 'error'); },
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api(`${base}/${platform}`, { method: 'PATCH', body: { enabled } }),
    onSuccess: onChanged,
  });

  const Icon = platform === 'android' ? Smartphone : Apple;
  const title = (
    <span className="inline-flex items-center gap-2">
      <span className="grid size-7 place-items-center rounded-md bg-muted"><Icon className="size-4" /></span>
      {platform === 'android' ? 'Android — Firebase Cloud Messaging' : 'iOS — Apple Push Notification service'}
    </span>
  );
  return (
    <SectionCard
      title={title}
      actions={current && !editing ? (
        <>
          <LoadingButton variant="outline" size="sm" loading={toggle.isPending} onClick={() => toggle.mutate(!current.enabled)}><Power /> {current.enabled ? 'Disable' : 'Enable'}</LoadingButton>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Replace /> Replace</Button>
          <LoadingButton size="sm" loading={verify.isPending} onClick={() => verify.mutate()}><ShieldCheck /> Verify</LoadingButton>
        </>
      ) : null}
    >
      <div className="space-y-4">
        {current && !editing && (
          <dl className="grid gap-x-8 sm:grid-cols-2">
            <Row k="Status">{!current.enabled ? <ToneBadge>disabled</ToneBadge> : current.verified_at ? <ToneBadge tone="green" dot>verified {fmtDate(current.verified_at)}</ToneBadge> : <ToneBadge tone="amber" dot>not verified</ToneBadge>}</Row>
            <Row k="Fingerprint"><span className="font-mono text-xs">{current.fingerprint}</span></Row>
            {platform === 'android' && environment.android && <Row k="Project ID"><span className="font-mono text-xs">{environment.android.fcm_project_id}</span></Row>}
            {platform === 'ios' && environment.ios && (
              <>
                <Row k="Key ID / Team ID"><span className="font-mono text-xs">{environment.ios.key_id} / {environment.ios.team_id_apple}</span></Row>
                <Row k="Bundle ID"><span className="font-mono text-xs">{environment.ios.bundle_id}</span></Row>
              </>
            )}
          </dl>
        )}
        {editing && (
          <>
            {platform === 'android' ? <AndroidForm value={android} onChange={setAndroid} /> : <IosForm value={ios} onChange={setIos} />}
            <div className="flex justify-end gap-2">
              {current && <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
              <LoadingButton loading={save.isPending} onClick={() => save.mutate()}>Save credentials</LoadingButton>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

const Row = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="flex items-center justify-between gap-4 border-b py-2.5"><dt className="text-sm text-muted-foreground">{k}</dt><dd className="text-sm">{children}</dd></div>
);
